/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {Module} from "@intuitionrobotics/ts-common";
import {ThunderDispatcher} from "@intuitionrobotics/thunderstorm/frontend";

export type CookieConsentDecision = "accepted" | "declined";

export interface OnCookieConsentChanged {
    __onCookieConsentChanged: () => void;
}

export const dispatch_onCookieConsentChanged =
    new ThunderDispatcher<OnCookieConsentChanged, "__onCookieConsentChanged">("__onCookieConsentChanged");

// Bump when the cookie/privacy policy materially changes so users are re-prompted.
const CONSENT_VERSION = "1";
const STORAGE_KEY = "cc.cookieConsent";

type StoredConsent = {
    decision: CookieConsentDecision;
    version: string;
    timestamp: number;
};

class CookieConsentModule_Class
    extends Module {

    private stored?: StoredConsent;

    constructor() {
        super("CookieConsentModule");
    }

    protected init(): void {
        this.stored = this.read();
    }

    private clearInvalidStoredConsent() {
        try {
            localStorage.removeItem(STORAGE_KEY);
        } catch {
        }
    }

    private isStoredConsent(value: unknown): value is StoredConsent {
        if (!value || typeof value !== "object")
            return false;

        const parsed = value as Record<string, unknown>;
        return parsed.version === CONSENT_VERSION
            && (parsed.decision === "accepted" || parsed.decision === "declined")
            && typeof parsed.timestamp === "number"
            && Number.isFinite(parsed.timestamp);
    }

    private read(): StoredConsent | undefined {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw)
                return undefined;

            const parsed: unknown = JSON.parse(raw);
            if (!this.isStoredConsent(parsed)) {
                this.clearInvalidStoredConsent();
                return undefined;
            }

            return parsed;
        } catch {
            return undefined;
        }
    }

    private write(decision: CookieConsentDecision) {
        const stored: StoredConsent = {decision, version: CONSENT_VERSION, timestamp: Date.now()};
        this.stored = stored;
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
        } catch (e) {
            this.logError("Failed to persist cookie consent", e);
        }
        dispatch_onCookieConsentChanged.dispatchUI();
    }

    needsDecision(): boolean {
        return !this.stored;
    }

    accept = () => this.write("accepted");
    decline = () => this.write("declined");

    hasAnalyticsConsent(): boolean {
        return this.stored?.decision === "accepted";
    }
}

export const CookieConsentModule = new CookieConsentModule_Class();
